import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOUCHERS = [
  { id: "essential", name: "Essential", price: "2 999", value: "4 499", save: "1 500" },
  { id: "prestige", name: "Prestige", price: "6 999", value: "10 499", save: "3 500" },
  { id: "supreme", name: "Supreme", price: "9 999", value: "14 999", save: "5 000" },
  { id: "allin", name: "All In", price: "13 990", value: "20 990", save: "7 000" },
];

export const ReservationForm = ({ onReserved, remaining }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    voucher_type: "prestige",
    note: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const onVoucherSelect = (e) => {
      const id = e?.detail;
      if (["essential", "prestige", "supreme"].includes(id)) {
        setForm((f) => ({ ...f, voucher_type: id }));
      }
    };
    window.addEventListener("carspa:select-voucher", onVoucherSelect);
    return () => window.removeEventListener("carspa:select-voucher", onVoucherSelect);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Potvrďte, prosím, souhlas se zpracováním údajů.");
      return;
    }
    if (!form.name || !form.email || !form.phone) {
      toast.error("Vyplňte jméno, e-mail a telefon.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/reservations`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        voucher_type: form.voucher_type,
        note: form.note || null,
      });
      setSuccess(data);
      toast.success("Rezervace přijata. Potvrzení míří na váš e-mail.");
      if (onReserved) onReserved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ")
          : "Rezervaci se nepodařilo odeslat. Zkuste to prosím znovu.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="rezervace" data-testid="form-section" className="relative py-20 sm:py-24 md:py-36 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 grid grid-cols-12 gap-y-10 lg:gap-12">
        <Reveal className="col-span-12 lg:col-span-5">
          <div className="text-[10px] sm:text-[11px] tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#D4AF37] mb-5 md:mb-6">
            08 — Rezervace
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white font-light leading-[1.05]">
            Zajistěte si voucher
            <br />
            <span className="italic text-gold-gradient">do konce června.</span>
          </h2>
          <p className="mt-5 md:mt-6 text-sm sm:text-base text-zinc-400 font-light leading-relaxed max-w-md">
            Vyplňte krátký formulář. Do 24 hodin vám zašleme platební údaje a obchodní podmínky.
            Rezervace je nezávazná a zdarma — platíte až po jejich obdržení.
          </p>
          <div className="mt-8 md:mt-10 gold-border p-5 sm:p-6">
            <div className="flex items-center gap-4">
              <div className="font-display text-4xl sm:text-5xl text-gold-gradient digit-stack">
                {remaining ?? "—"}
              </div>
              <div>
                <div className="text-[10px] sm:text-[11px] tracking-[0.25em] sm:tracking-[0.3em] uppercase text-zinc-400">
                  voucherů zbývá
                </div>
                <div className="text-xs text-zinc-500 mt-1">Platnost 3 roky · Přenositelné</div>
              </div>
            </div>
          </div>

          <div className="mt-6 md:mt-8" data-testid="payment-methods-block">
            <div className="text-[10px] tracking-[0.3em] sm:tracking-[0.35em] uppercase text-zinc-500 mb-3">
              Akceptujeme platbu
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-zinc-300">
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <span className="w-7 h-5 border border-[#D4AF37]/50 flex items-center justify-center">
                  <span className="w-3 h-px bg-[#D4AF37]" />
                </span>
                Kartou
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <span className="w-7 h-5 border border-[#D4AF37]/50 flex items-center justify-center text-[7px] tracking-widest text-[#D4AF37]">
                  FA
                </span>
                Fakturou
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <span className="inline-flex items-center justify-center bg-white px-1.5 py-1 rounded-sm">
                  <img
                    src="/brand/ccs-logo.svg"
                    alt="Karta CCS"
                    width="40"
                    height="27"
                    className="h-[18px] w-auto"
                    loading="lazy"
                    decoding="async"
                  />
                </span>
                Kartou CCS
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={150} className="col-span-12 lg:col-span-7">
          {success ? (
            <div data-testid="reservation-success" className="gold-border p-8 sm:p-10 md:p-14 text-center">
              <div className="text-[10px] sm:text-[11px] tracking-[0.35em] sm:tracking-[0.4em] uppercase text-[#D4AF37] mb-4">
                Rezervace potvrzena
              </div>
              <h3 className="font-display text-2xl sm:text-3xl md:text-4xl text-white font-light leading-tight">
                Děkujeme, {success.name}.
              </h3>
              <p className="mt-4 sm:mt-5 text-sm sm:text-base text-zinc-400 leading-relaxed max-w-md mx-auto">
                Na e-mail <strong className="text-white">{success.email}</strong> míří potvrzení
                s detaily k voucheru <strong className="text-[#D4AF37]">{success.voucher_name}</strong>.
                Do 24 hodin vám zašleme platební údaje a obchodní podmínky.
              </p>
              <div className="hairline my-8 max-w-xs mx-auto" />
              <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                CarSpa · Čestlice · Butovice
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="reservation-form"
              className="gold-border p-6 sm:p-8 md:p-10 space-y-5 md:space-y-6"
              noValidate
            >
              {/* Voucher selector */}
              <div>
                <div className="text-[10px] tracking-[0.3em] sm:tracking-[0.35em] uppercase text-zinc-400 mb-3">
                  Vyberte voucher
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3" role="radiogroup">
                  {VOUCHERS.map((v) => {
                    const active = form.voucher_type === v.id;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, voucher_type: v.id }))}
                        data-testid={`form-voucher-${v.id}`}
                        aria-pressed={active}
                        className={`text-left p-3 sm:p-4 border transition-colors ${
                          active
                            ? "border-[#D4AF37] bg-[#D4AF37]/10"
                            : "border-white/10 hover:border-white/25"
                        }`}
                      >
                        <div className={`text-[9px] sm:text-[10px] tracking-[0.25em] sm:tracking-[0.3em] uppercase mb-1 ${active ? "text-[#D4AF37]" : "text-zinc-500"}`}>
                          {v.name}
                        </div>
                        <div className="font-display text-base sm:text-xl text-white leading-none">{v.price}<span className="text-[10px] sm:text-xs text-zinc-400 ml-1">Kč</span></div>
                        <div className="text-[9px] sm:text-[10px] text-zinc-500 mt-1 line-through decoration-red-500/70">{v.value} Kč</div>
                        <div className={`text-[9px] sm:text-[10px] mt-0.5 font-semibold ${active ? "text-[#D4AF37]" : "text-[#D4AF37]/70"}`}>
                          −{v.save} Kč
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                <label className="block">
                  <span className="block text-[10px] tracking-[0.35em] uppercase text-zinc-400 mb-2">Jméno a příjmení</span>
                  <input
                    data-testid="form-name-input"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    required
                    autoComplete="name"
                    className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#D4AF37] focus:outline-none py-3 text-white placeholder:text-zinc-600 transition-colors"
                    placeholder="Jan Novák"
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] tracking-[0.35em] uppercase text-zinc-400 mb-2">Telefon</span>
                  <input
                    data-testid="form-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    required
                    autoComplete="tel"
                    className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#D4AF37] focus:outline-none py-3 text-white placeholder:text-zinc-600 transition-colors"
                    placeholder="+420 777 123 456"
                  />
                </label>
              </div>

              <label className="block">
                <span className="block text-[10px] tracking-[0.35em] uppercase text-zinc-400 mb-2">E-mail</span>
                <input
                  data-testid="form-email-input"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  required
                  autoComplete="email"
                  className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#D4AF37] focus:outline-none py-3 text-white placeholder:text-zinc-600 transition-colors"
                  placeholder="jan.novak@email.cz"
                />
              </label>

              <label className="block">
                <span className="block text-[10px] tracking-[0.35em] uppercase text-zinc-400 mb-2">Poznámka (nepovinné)</span>
                <textarea
                  data-testid="form-note-input"
                  value={form.note}
                  onChange={update("note")}
                  rows={3}
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#D4AF37] focus:outline-none py-3 text-white placeholder:text-zinc-600 transition-colors resize-none"
                  placeholder="Model vozu, preferovaná pobočka, dotazy…"
                />
              </label>

              <label className="flex items-start gap-3 pt-2 cursor-pointer select-none">
                <input
                  data-testid="form-consent-checkbox"
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                  className="mt-1 w-4 h-4 accent-[#D4AF37] bg-transparent"
                />
                <span className="text-xs text-zinc-400 leading-relaxed">
                  Souhlasím se zpracováním osobních údajů za účelem vyřízení rezervace
                  voucheru. Údaje nejsou předávány třetím stranám.
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                data-testid="form-submit-button"
                className="btn-gold w-full bg-[#D4AF37] hover:bg-[#F0C846] disabled:opacity-50 disabled:cursor-not-allowed text-black px-6 sm:px-8 py-4 sm:py-5 text-[11px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase font-semibold transition-colors"
              >
                {submitting ? "Odesílám…" : "Zajistit si voucher"}
              </button>

              <p className="text-[11px] text-zinc-500 text-center">
                Rezervace je nezávazná. Platbu provádíte až po obdržení obchodních podmínek a platebních údajů.
                Obchodní podmínky k voucheru obdržíte e-mailem ihned po odeslání formuláře.
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default ReservationForm;
